import { useEffect, useState } from "react";
import {
  Box, Typography, Chip, CircularProgress,
  Alert, TextField, Tooltip, IconButton,
} from "@mui/material";
import { Check, CirclePlus, ChartNoAxesCombined, TrendingUp, TrendingDown, Minus, Info } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, LabelList, ReferenceLine, ReferenceArea,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../../../../provider";
import { useTranslation } from "../../../../../language";
import axios from "axios";
import { WEIGHTDATA, API_URL } from "../../../../../constants";
import CustomDialogSmall from "../../../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../../../components/Dialog/DiaCompMedium";
import ButtonForm from "../../../../../components/Button/ButtonForm";
import DiaBodyInfo from "./DiaBodyInfo";

// ─── Weight chart ─────────────────────────────────────────────────────────────

function WeightChartContent({ record, theme, t, heightCm }) {
  const chartPoints = [...record]
    .filter(r => r.weight_kg != null)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(r => ({
      id:    r.id,
      date:  r.date,
      label: new Date(r.date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric", year: "2-digit" }),
      value: parseFloat(r.weight_kg),
      bmi:   r.bmi != null ? parseFloat(r.bmi) : null,
    }));

  if (!chartPoints.length) return (
    <Typography sx={{ p: 3 }} color="text.secondary">{t("vitals_no_chart_data")}</Typography>
  );

  const targetWeight = (heightCm > 0)
    ? parseFloat((25 * Math.pow(heightCm / 100, 2)).toFixed(1))
    : null;

  const values  = chartPoints.map(d => d.value);
  const allNums = [...values, targetWeight].filter(x => x != null);
  const span    = Math.max(...allNums) - Math.min(...allNums) || 1;
  const pad     = span * 0.3;
  const yMin    = parseFloat((Math.min(...allNums) - pad).toFixed(1));
  const yMax    = parseFloat((Math.max(...allNums) + pad).toFixed(1));
  const primary = theme.palette.primary.main;
  const green   = theme.palette.traffic.green;
  const red     = theme.palette.traffic.red;

  const CustomDot = ({ cx, cy }) => (
    <circle cx={cx} cy={cy} r={6} fill={primary}
      stroke={theme.palette.background.paper} strokeWidth={2} />
  );

  const ValueLabel = ({ x, y, value }) => (
    <text x={x} y={y - 13} textAnchor="middle" fontSize={11} fontWeight={700}
      fill={theme.palette.text.primary}>
      {Number(value).toFixed(1)}
    </text>
  );

  return (
    <Box sx={{ width: "100%", pt: 4, px: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Chip label={`${t("bt_chart_unit")}: kg`} size="small"
          sx={{ fontWeight: 700, fontSize: "0.8rem", letterSpacing: 0.3 }} />
      </Box>

      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartPoints} margin={{ top: 30, right: 28, left: 4, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          {targetWeight != null && (
            <ReferenceLine y={targetWeight}
              stroke={green} strokeDasharray="5 3" strokeWidth={1.5}
              label={{ value: `BMI 25 → ${targetWeight} kg`, position: "insideTopRight", fontSize: 10, fill: green, fontWeight: 600 }}
            />
          )}
          <XAxis dataKey="label"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} padding={{ left: 32, right: 32 }} />
          <YAxis domain={[yMin, yMax]}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} width={48}
            tickFormatter={v => Number(v).toFixed(1)} />
          <RechartsTooltip
            contentStyle={{
              borderRadius: 10, border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13,
            }}
            formatter={value => [`${Number(value).toFixed(1)} kg`, t("bt_chart_value")]}
          />
          <Line type="monotone" dataKey="value" stroke={primary} strokeWidth={2.5}
            dot={<CustomDot />} activeDot={{ r: 9, stroke: primary, strokeWidth: 2 }}>
            <LabelList dataKey="value" content={<ValueLabel />} />
          </Line>
        </LineChart>
      </ResponsiveContainer>

      {/* History table */}
      <Box sx={{ mt: 3, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
        <Box sx={{
          display: "grid", gridTemplateColumns: "1.6fr 1fr 0.8fr 0.6fr",
          px: 2, py: 1,
          bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
        }}>
          {[t("bt_chart_col_date"), t("bt_chart_col_value"), "BMI", t("bt_chart_col_trend")].map(h => (
            <Typography key={h} variant="caption"
              sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", fontSize: "0.7rem" }}>
              {h}
            </Typography>
          ))}
        </Box>
        {[...chartPoints].reverse().map((row, idx, arr) => {
          const prev  = arr[idx + 1];
          const delta = prev != null ? row.value - prev.value : null;
          const TrendIcon = delta == null ? null
            : Math.abs(delta) < 0.01 ? Minus
            : delta > 0 ? TrendingUp : TrendingDown;
          const trendColor = delta == null
            ? theme.palette.text.disabled
            : Math.abs(delta) < 0.01
              ? theme.palette.text.secondary
              : delta > 0 ? red : green;
          const isEven = idx % 2 === 0;
          return (
            <Box key={row.id} sx={{
              display: "grid", gridTemplateColumns: "1.6fr 1fr 0.8fr 0.6fr",
              px: 2, py: 0.5, alignItems: "center",
              bgcolor: isEven
                ? (theme.palette.mode === "light" ? "grey.C50"  : "greydark.C900")
                : (theme.palette.mode === "light" ? "grey.C100" : "greydark.C900"),
            }}>
              <Typography variant="body2" color="text.secondary">
                {new Date(row.date + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {row.value.toFixed(1)} kg
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {row.bmi != null ? row.bmi.toFixed(1) : "—"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {TrendIcon
                  ? <TrendIcon size={16} color={trendColor} strokeWidth={2.5} />
                  : <Minus size={16} color={theme.palette.text.disabled} strokeWidth={2} />
                }
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── BMI chart ────────────────────────────────────────────────────────────────

const BMI_TARGET = 25;
const WHTR_TARGET = 0.5;

function BmiChartContent({ record, theme, t }) {
  const chartPoints = [...record]
    .filter(r => r.bmi != null)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(r => ({
      id:     r.id,
      date:   r.date,
      label:  new Date(r.date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric", year: "2-digit" }),
      value:  parseFloat(r.bmi),
      status: parseFloat(r.bmi) < 18.5 ? "LOW" : parseFloat(r.bmi) < BMI_TARGET ? "NORMAL" : "HIGH",
    }));

  if (!chartPoints.length) return (
    <Typography sx={{ p: 3 }} color="text.secondary">{t("vitals_no_chart_data")}</Typography>
  );

  const values  = chartPoints.map(d => d.value);
  const allNums = [...values, BMI_TARGET];
  const span    = Math.max(...allNums) - Math.min(...allNums) || 1;
  const pad     = span * 0.3;
  const yMin    = parseFloat((Math.min(...allNums) - pad).toFixed(1));
  const yMax    = parseFloat((Math.max(...allNums) + pad).toFixed(1));
  const primary = theme.palette.primary.main;
  const green   = theme.palette.traffic.green;
  const red     = theme.palette.traffic.red;
  const orange  = theme.palette.traffic.orange;

  const CustomDot = ({ cx, cy, payload }) => (
    <circle cx={cx} cy={cy} r={6}
      fill={payload.status === "NORMAL" ? green : payload.status === "LOW" ? orange : red}
      stroke={theme.palette.background.paper} strokeWidth={2} />
  );

  const ValueLabel = ({ x, y, value }) => (
    <text x={x} y={y - 13} textAnchor="middle" fontSize={11} fontWeight={700}
      fill={theme.palette.text.primary}>
      {Number(value).toFixed(1)}
    </text>
  );

  return (
    <Box sx={{ width: "100%", pt: 4, px: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Chip label={`${t("vitals_bmi_ref")}`} size="small"
          sx={{ fontWeight: 700, fontSize: "0.8rem", letterSpacing: 0.3, bgcolor: green, color: "#fff" }} />
      </Box>

      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartPoints} margin={{ top: 30, right: 28, left: 4, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          <ReferenceLine y={BMI_TARGET} stroke={green} strokeDasharray="5 3" strokeWidth={1.5}
            label={{ value: `${t("vitals_chart_target")}: BMI ${BMI_TARGET}`, position: "insideTopRight", fontSize: 10, fill: green, fontWeight: 600 }} />
          <XAxis dataKey="label"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} padding={{ left: 32, right: 32 }} />
          <YAxis domain={[yMin, yMax]}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} width={48}
            tickFormatter={v => Number(v).toFixed(1)} />
          <RechartsTooltip
            contentStyle={{
              borderRadius: 10, border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13,
            }}
            formatter={value => [`${Number(value).toFixed(1)}`, t("bt_chart_value")]}
          />
          <Line type="monotone" dataKey="value" stroke={primary} strokeWidth={2.5}
            dot={<CustomDot />} activeDot={{ r: 9, stroke: primary, strokeWidth: 2 }}>
            <LabelList dataKey="value" content={<ValueLabel />} />
          </Line>
        </LineChart>
      </ResponsiveContainer>

      {/* History table */}
      <Box sx={{ mt: 3, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
        <Box sx={{
          display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr",
          px: 2, py: 1,
          bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
        }}>
          {[t("bt_chart_col_date"), "BMI", t("bt_chart_col_status")].map(h => (
            <Typography key={h} variant="caption"
              sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", fontSize: "0.7rem" }}>
              {h}
            </Typography>
          ))}
        </Box>
        {[...chartPoints].reverse().map((row, idx) => {
          const isNormal = row.status === "NORMAL";
          const isEven   = idx % 2 === 0;
          return (
            <Box key={row.id} sx={{
              display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr",
              px: 2, py: 0.5, alignItems: "center",
              bgcolor: isEven
                ? (theme.palette.mode === "light" ? "grey.C50"  : "greydark.C900")
                : (theme.palette.mode === "light" ? "grey.C100" : "greydark.C900"),
            }}>
              <Typography variant="body2" color="text.secondary">
                {new Date(row.date + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {row.value.toFixed(1)}
              </Typography>
              {isNormal ? (
                <Check size={16} strokeWidth={3} color={green} />
              ) : (
                <Typography variant="body2" fontWeight={700} sx={{ color: red }}>
                  {t("vitals_status_high")}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Waist chart ──────────────────────────────────────────────────────────────

function WaistChartContent({ record, theme, t, gender }) {
  const threshold = gender === "F" ? 88 : 102;

  const chartPoints = [...record]
    .filter(r => r.waist_cm != null)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(r => ({
      id:     r.id,
      date:   r.date,
      label:  new Date(r.date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric", year: "2-digit" }),
      value:  parseFloat(r.waist_cm),
      status: parseFloat(r.waist_cm) < threshold ? "NORMAL" : "HIGH",
    }));

  if (!chartPoints.length) return (
    <Typography sx={{ p: 3 }} color="text.secondary">{t("vitals_no_chart_data")}</Typography>
  );

  const values  = chartPoints.map(d => d.value);
  const allNums = [...values, threshold];
  const span    = Math.max(...allNums) - Math.min(...allNums) || 1;
  const pad     = span * 0.3;
  const yMin    = parseFloat((Math.min(...allNums) - pad).toFixed(1));
  const yMax    = parseFloat((Math.max(...allNums) + pad).toFixed(1));
  const primary = theme.palette.primary.main;
  const green   = theme.palette.traffic.green;
  const red     = theme.palette.traffic.red;

  const CustomDot = ({ cx, cy, payload }) => (
    <circle cx={cx} cy={cy} r={6}
      fill={payload.status === "NORMAL" ? green : red}
      stroke={theme.palette.background.paper} strokeWidth={2} />
  );

  const ValueLabel = ({ x, y, value }) => (
    <text x={x} y={y - 13} textAnchor="middle" fontSize={11} fontWeight={700}
      fill={theme.palette.text.primary}>
      {Number(value).toFixed(1)}
    </text>
  );

  const refLabel = gender === "F" ? t("vitals_waist_ref_f") : t("vitals_waist_ref_m");

  return (
    <Box sx={{ width: "100%", pt: 4, px: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Chip label={refLabel} size="small"
          sx={{ fontWeight: 700, fontSize: "0.8rem", letterSpacing: 0.3, bgcolor: green, color: "#fff" }} />
      </Box>

      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartPoints} margin={{ top: 30, right: 28, left: 4, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          <ReferenceLine y={threshold} stroke={green} strokeDasharray="5 3" strokeWidth={1.5}
            label={{ value: `${t("vitals_chart_target")}: ${threshold} cm`, position: "insideTopRight", fontSize: 10, fill: green, fontWeight: 600 }} />
          <XAxis dataKey="label"
            tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} padding={{ left: 32, right: 32 }} />
          <YAxis domain={[yMin, yMax]}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} width={48}
            tickFormatter={v => Number(v).toFixed(1)} />
          <RechartsTooltip
            contentStyle={{
              borderRadius: 10, border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13,
            }}
            formatter={value => [`${Number(value).toFixed(1)} cm`, t("bt_chart_value")]}
          />
          <Line type="monotone" dataKey="value" stroke={primary} strokeWidth={2.5}
            dot={<CustomDot />} activeDot={{ r: 9, stroke: primary, strokeWidth: 2 }}>
            <LabelList dataKey="value" content={<ValueLabel />} />
          </Line>
        </LineChart>
      </ResponsiveContainer>

      {/* History table */}
      <Box sx={{ mt: 3, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
        <Box sx={{
          display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr",
          px: 2, py: 1,
          bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
        }}>
          {[t("bt_chart_col_date"), `${t("vitals_waist")} (cm)`, t("bt_chart_col_status")].map(h => (
            <Typography key={h} variant="caption"
              sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", fontSize: "0.7rem" }}>
              {h}
            </Typography>
          ))}
        </Box>
        {[...chartPoints].reverse().map((row, idx) => {
          const isNormal = row.status === "NORMAL";
          const isEven   = idx % 2 === 0;
          return (
            <Box key={row.id} sx={{
              display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr",
              px: 2, py: 0.5, alignItems: "center",
              bgcolor: isEven
                ? (theme.palette.mode === "light" ? "grey.C50"  : "greydark.C900")
                : (theme.palette.mode === "light" ? "grey.C100" : "greydark.C900"),
            }}>
              <Typography variant="body2" color="text.secondary">
                {new Date(row.date + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {row.value.toFixed(1)} cm
              </Typography>
              {isNormal ? (
                <Check size={16} strokeWidth={3} color={green} />
              ) : (
                <Typography variant="body2" fontWeight={700} sx={{ color: red }}>
                  {t("vitals_status_high")}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Evaluation helpers ───────────────────────────────────────────────────────

function evalBmi(v) {
  if (v == null) return null;
  if (v < 18.5) return "LOW";
  return v < BMI_TARGET ? "NORMAL" : "HIGH";
}

function evalWaist(v, gender) {
  if (v == null) return null;
  const threshold = gender === "F" ? 88 : 102;
  return v < threshold ? "NORMAL" : "HIGH";
}

function evalWhtr(v) {
  if (v == null) return null;
  return v < WHTR_TARGET ? "NORMAL" : "HIGH";
}

function statusColor(status, theme) {
  if (status === "NORMAL") return theme.palette.traffic.green;
  if (status === "HIGH")   return theme.palette.traffic.red;
  if (status === "LOW")    return theme.palette.traffic.orange;
  return theme.palette.grey.C400;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function GetBmi() {
  const theme  = useTheme();
  const { t }  = useTranslation();
  const { state } = useAuth();
  const gender = state.user?.gender;

  const [record,  setRecord]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // ── Height state ─────────────────────────────────────────────────────────────
  const [heightCm, setHeightCm] = useState(null);
  const [heightOpen,    setHeightOpen]    = useState(false);
  const [heightInput,   setHeightInput]   = useState("");
  const [heightLoading, setHeightLoading] = useState(false);
  const [heightError,   setHeightError]   = useState("");
  const [heightSuccess, setHeightSuccess] = useState(false);

  const [chartOpen,      setChartOpen]      = useState(false);
  const [bmiChartOpen,   setBmiChartOpen]   = useState(false);
  const [waistChartOpen, setWaistChartOpen] = useState(false);
  const [whtrInfoOpen,   setWhtrInfoOpen]   = useState(false);

  // ── Weight add state ─────────────────────────────────────────────────────────
  const [weightOpen,    setWeightOpen]    = useState(false);
  const [weightDate,    setWeightDate]    = useState("");
  const [weightValue,   setWeightValue]   = useState("");
  const [weightWaist,   setWeightWaist]   = useState("");
  const [weightLoading, setWeightLoading] = useState(false);
  const [weightError,   setWeightError]   = useState("");
  const [weightSuccess, setWeightSuccess] = useState(false);

  const loadRecords = () => axios.get(WEIGHTDATA).then(res => {
    const data = Array.isArray(res.data) ? res.data : [];
    setRecord(data);
    const latest = data[data.length - 1];
    if (latest?.height_cm > 0) setHeightCm(parseFloat(latest.height_cm));
  });

  useEffect(() => {
    loadRecords()
      .then(() => setLoading(false))
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const handleHeightSubmit = async () => {
    const val = parseFloat(heightInput);
    if (!val || val < 50 || val > 250) {
      setHeightError(t("vitals_height_error"));
      return;
    }
    setHeightLoading(true);
    setHeightError("");
    try {
      await axios.patch(`${API_URL}/api/auth/update_heightcm/`, { height_cm: val });
      setHeightCm(val);
      setHeightSuccess(true);
      // whtr is calculated from the current height on every read
      loadRecords().catch(() => {});
      setTimeout(() => {
        setHeightOpen(false);
        setHeightSuccess(false);
        setHeightInput("");
      }, 1400);
    } catch (err) {
      setHeightError(
        err.response?.data?.height_cm?.[0] ?? t("vitals_height_save_error")
      );
    } finally {
      setHeightLoading(false);
    }
  };

  const closeHeightDialog = () => {
    setHeightOpen(false);
    setHeightError("");
    setHeightInput("");
    setHeightSuccess(false);
  };

  const openWeightDialog = () => {
    const latest = record[record.length - 1] ?? {};
    setWeightDate(new Date().toISOString().split("T")[0]);
    setWeightValue("");
    setWeightWaist(latest.waist_cm != null ? String(latest.waist_cm) : "");
    setWeightError("");
    setWeightSuccess(false);
    setWeightOpen(true);
  };

  const handleWeightSubmit = async () => {
    if (!weightDate || !weightValue || !weightWaist) {
      setWeightError(t("vitals_fill_all_fields"));
      return;
    }
    setWeightLoading(true);
    setWeightError("");
    try {
      await axios.post(WEIGHTDATA, {
        date:      weightDate,
        weight_kg: parseFloat(weightValue),
        waist_cm:  parseFloat(weightWaist),
      });
      setWeightSuccess(true);
      // Refresh displayed data
      loadRecords().catch(() => {});
      setTimeout(() => { setWeightOpen(false); setWeightSuccess(false); }, 1400);
    } catch (err) {
      const d = err.response?.data;
      setWeightError(
        typeof d === "string" ? d :
        d?.detail ?? d?.weight_kg?.[0] ?? d?.waist_cm?.[0] ?? d?.height_cm?.[0] ?? t("vitals_save_error_generic")
      );
    } finally {
      setWeightLoading(false);
    }
  };

  // ── Early returns ─────────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
      <CircularProgress size={18} />
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ mx: 2.5, my: 1 }}>
      <Typography variant="body2">{error}</Typography>
    </Alert>
  );

  // ── Derive values ─────────────────────────────────────────────────────────────
  const latest    = record[record.length - 1] ?? {};
  const weight    = latest.weight_kg ?? null;
  const bmi       = latest.bmi      ?? null;
  const waist     = latest.waist_cm ?? null;
  const whtr      = latest.whtr     ?? null;
  const hasHeight = heightCm != null && heightCm > 0;

  const rows = [
    {
      key:      "height",
      name:     t("vitals_height"),
      sub:      "cm",
      refRange: null,
      value:    hasHeight ? `${heightCm}` : "—",
      status:   null,
      missing:  !hasHeight,
    },
    {
      key:      "weight",
      name:     t("vitals_weight"),
      sub:      "kg",
      refRange: null,
      value:    weight != null ? `${weight}` : "—",
      status:   null,
    },
    {
      key:      "bmi",
      name:     t("vitals_bmi"),
      sub:      "kg / m²",
      refRange: t("vitals_bmi_ref"),
      value:    bmi != null ? `${bmi}` : "—",
      status:   evalBmi(bmi),
      swapped:  true,
    },
    {
      key:      "waist",
      name:     t("vitals_waist"),
      sub:      "cm",
      refRange: gender === "F" ? t("vitals_waist_ref_f") : t("vitals_waist_ref_m"),
      value:    waist != null ? `${waist}` : "—",
      status:   evalWaist(waist, gender),
      swapped:  true,
    },
    {
      key:      "whtr",
      name:     t("vitals_whtr"),
      sub:      "cm",
      refRange: t("vitals_whtr_ref"),
      value:    whtr != null ? `${whtr}` : "—",
      status:   evalWhtr(whtr),
      swapped:  true,
    },
  ];

  return (
    <>
      {rows.map((row, i) => {
        const color    = statusColor(row.status, theme);
        const barColor = (row.key === "height" && row.missing)
          ? theme.palette.traffic.orange
          : row.status == null ? theme.palette.grey.C400 : color;

        const isHeight = row.key === "height";
        const isWeight = row.key === "weight";
        const isWaist  = row.key === "waist";
        const isBmi    = row.key === "bmi";
        const isWhtr   = row.key === "whtr";

        return (
          <Box key={row.key} sx={{
            bgcolor: i % 2 === 0
              ? (theme.palette.mode === "light" ? "grey.C50"  : "greydark.C900")
              : (theme.palette.mode === "light" ? "grey.C100" : "greydark.C900"),
          }}>
            <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 0.75, gap: { xs: 1, sm: 3 }, flexWrap: "nowrap" }}>

              {/* Status dot + name/unit caption below */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexGrow: 1, minWidth: { xs: 0, sm: 120 } }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: barColor, mt: "7px" }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={500}>{row.name}</Typography>
                  {row.sub && (
                    <Typography variant="caption" color="text.disabled">{t("vitals_unit")}: {row.sub}</Typography>
                  )}
                </Box>
              </Box>

              {/* Reference range — centered column, same weight as name (hidden on mobile to keep value + icon next to the title) */}
              <Box sx={{ minWidth: 90, textAlign: "center", flexShrink: 0, display: { xs: "none", sm: "block" } }}>
                {row.refRange && (
                  <Typography variant="caption" fontWeight={500} color="text.secondary">
                    {row.refRange}
                  </Typography>
                )}
              </Box>

              {/* Status badge — before value when swapped (BMI, waist) */}
              {row.swapped && !isHeight && !isWeight && (
                <Box sx={{ minWidth: { xs: "auto", sm: 80 }, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                  {row.status === "NORMAL" ? (
                    <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                  ) : row.status != null ? (
                    <Chip label={row.status === "HIGH" ? t("vitals_status_high") : t("vitals_status_low")} size="small"
                      sx={{ bgcolor: color, color: "#fff", fontWeight: 700, fontSize: "0.65rem", height: 22, borderRadius: "6px", "& .MuiChip-label": { px: 1 } }}
                    />
                  ) : null}
                </Box>
              )}

              {/* Value area — height gets Pen, weight/waist get Plus, others plain */}
              {isHeight ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1.5 } }}>
                  <Typography variant="body1" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {row.value}
                  </Typography>
                  <Tooltip title={t("vitals_set_height")} placement="top">
                    <IconButton size="small" onClick={() => setHeightOpen(true)}
                      sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                      <CirclePlus size={18} color={theme.palette.text.secondary} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : isWeight ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1.5 } }}>
                  <Typography variant="body1" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {row.value}
                  </Typography>
                  <Tooltip title={t("vitals_weight_chart")} placement="top">
                    <IconButton size="small" onClick={() => setChartOpen(true)}
                      sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                      <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("vitals_add_measurement")} placement="top">
                    <IconButton size="small" onClick={openWeightDialog}
                      sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                      <CirclePlus size={18} color={theme.palette.text.secondary} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : isWaist ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1.5 } }}>
                  <Typography variant="body1" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {row.value}
                  </Typography>
                  <Tooltip title={t("vitals_waist_chart")} placement="top">
                    <IconButton size="small" onClick={() => setWaistChartOpen(true)}
                      sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                      <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("vitals_add_measurement")} placement="top">
                    <IconButton size="small" onClick={openWeightDialog}
                      sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                      <CirclePlus size={18} color={theme.palette.text.secondary} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : isBmi ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1.5 } }}>
                  <Typography variant="body1" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {row.value}
                  </Typography>
                  <Tooltip title={t("vitals_bmi_chart")} placement="top">
                    <IconButton size="small" onClick={() => setBmiChartOpen(true)}
                      sx={{ "&:hover": { transform: "scale(1.15)" } }}>
                      <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
                    </IconButton>
                  </Tooltip>
                  <DiaBodyInfo />
                </Box>
              ) : isWhtr ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1.5 } }}>
                  <Typography variant="body1" fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {row.value}
                  </Typography>
                  <Tooltip title="Info" placement="right">
                    <IconButton size="small" onClick={() => setWhtrInfoOpen(true)}>
                      <Info size={18} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Typography variant="body1" fontWeight={500}
                  sx={{ minWidth: 80, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {row.value}
                </Typography>
              )}

              {/* Status badge — after value for non-swapped rows (not height or weight) */}
              {!row.swapped && !isHeight && !isWeight && (
                <Box sx={{ minWidth: { xs: "auto", sm: 80 }, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                  {row.status === "NORMAL" ? (
                    <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                  ) : row.status != null ? (
                    <Chip label={row.status === "HIGH" ? t("vitals_status_high") : t("vitals_status_low")} size="small"
                      sx={{ bgcolor: color, color: "#fff", fontWeight: 700, fontSize: "0.65rem", height: 22, borderRadius: "6px", "& .MuiChip-label": { px: 1 } }}
                    />
                  ) : null}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      {/* ── Weight chart dialog ──────────────────────────────────────────── */}
      <CustomDialog
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        title={t("vitals_weight_chart")}
        content={<WeightChartContent record={record} theme={theme} t={t} heightCm={heightCm} />}
      />

      {/* ── Waist chart dialog ───────────────────────────────────────────── */}
      <CustomDialog
        open={waistChartOpen}
        onClose={() => setWaistChartOpen(false)}
        title={t("vitals_waist_chart")}
        content={<WaistChartContent record={record} theme={theme} t={t} gender={gender} />}
      />

      {/* ── BMI chart dialog ─────────────────────────────────────────────── */}
      <CustomDialog
        open={bmiChartOpen}
        onClose={() => setBmiChartOpen(false)}
        title={t("vitals_bmi_chart")}
        content={<BmiChartContent record={record} theme={theme} t={t} />}
      />

      {/* ── WHtR info dialog ─────────────────────────────────────────────── */}
      <CustomDialog
        open={whtrInfoOpen}
        onClose={() => setWhtrInfoOpen(false)}
        title={t("vitals_whtr_info_title")}
        content={<Typography variant="body1" sx={{ pt: 2 }}>{t("vitals_whtr_info_body")}</Typography>}
      />

      {/* ── Weight add dialog ────────────────────────────────────────────── */}
      <CustomDialogSmall
        open={weightOpen}
        onClose={() => { setWeightOpen(false); setWeightError(""); setWeightSuccess(false); }}
        title={t("vitals_add_measurement")}
        content={
          <Box sx={{ pt: 1, pb: 1, px: 0.5 }}>
            {weightSuccess ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
                <Check size={20} color={theme.palette.traffic.green} strokeWidth={2.5} />
                <Typography fontWeight={500} color="success.main">{t("vitals_save_success")}</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 0.5 }}>
                  <TextField
                    label={t("vitals_date")} type="date" size="small"
                    value={weightDate}
                    onChange={e => { setWeightDate(e.target.value); setWeightError(""); }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label={`${t("vitals_weight")} (kg)`} type="number" size="small"
                    value={weightValue}
                    onChange={e => { setWeightValue(e.target.value); setWeightError(""); }}
                    inputProps={{ min: 1, max: 500, step: 0.1 }}
                    fullWidth
                  />
                  <TextField
                    label={`${t("vitals_waist")} (cm)`} type="number" size="small"
                    value={weightWaist}
                    onChange={e => { setWeightWaist(e.target.value); setWeightError(""); }}
                    inputProps={{ min: 1, max: 300, step: 0.1 }}
                    fullWidth
                    helperText={t("vitals_prefilled")}
                  />
                </Box>
                {weightError && (
                  <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
                    {weightError}
                  </Typography>
                )}
                <ButtonForm
                  name={weightLoading ? <CircularProgress size={18} color="inherit" /> : t("vitals_height_save")}
                  onClick={handleWeightSubmit}
                  disabled={weightLoading || !weightDate || !weightValue || !weightWaist}
                />
              </>
            )}
          </Box>
        }
      />

      {/* ── Height update dialog ──────────────────────────────────────────── */}
      <CustomDialogSmall
        open={heightOpen}
        onClose={closeHeightDialog}
        title={t("vitals_height_dialog_title")}
        content={
          <Box sx={{ pt: 1, pb: 1, px: 0.5 }}>
            {heightSuccess ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
                <Check size={20} color={theme.palette.traffic.green} strokeWidth={2.5} />
                <Typography fontWeight={500} color="success.main">
                  {t("vitals_height_success")}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
                  {t("vitals_height_dialog_body")}
                </Typography>
                <TextField
                  label={t("vitals_height_label")}
                  value={heightInput}
                  onChange={e => { setHeightInput(e.target.value); setHeightError(""); }}
                  type="number"
                  inputProps={{ min: 50, max: 250, step: 0.1 }}
                  size="small"
                  fullWidth
                  error={Boolean(heightError)}
                  helperText={heightError || " "}
                  sx={{ mb: 0.5 }}
                />
                <ButtonForm
                  name={heightLoading
                    ? <CircularProgress size={18} color="inherit" />
                    : t("vitals_height_save")}
                  onClick={handleHeightSubmit}
                  disabled={heightLoading || !heightInput}
                />
              </>
            )}
          </Box>
        }
      />
    </>
  );
}
